import { doc, getDoc, setDoc, Timestamp, collection, getDocs, query, where, addDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { badgeConfig } from "../config/badgeConfig";
import { createNotification } from "./notificationUtils";

// ⏱️ Kullanıcının streak bilgilerini getir
export const getStreaks = async (userId) => {
  try {
    const docRef = doc(db, "streaks", userId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists()
      ? snapshot.data()
      : { currentStreak: 0, longestStreak: 0, lastStreakDate: null };
  } catch (err) {
    console.error("Streak verileri çekilirken hata:", err);
    throw new Error("Streak verileri alınamadı.");
  }
};

// 🔁 Streak'i güncelle
export const updateStreak = async (userId, type) => {
  try {
    console.log(`updateStreak çağrıldı - userId: ${userId}, type: ${type}`);
    const docRef = doc(db, "streaks", userId);
    const userRef = doc(db, "users", userId);
    const snapshot = await getDoc(docRef);
    const userSnapshot = await getDoc(userRef);
    const today = new Date().toISOString().split("T")[0];

    let data = snapshot.exists()
      ? snapshot.data()
      : { currentStreak: 0, longestStreak: 0, lastStreakDate: null };
    let userData = userSnapshot.exists()
      ? userSnapshot.data()
      : { weeklyStreakPoints: 0, monthlyStreakPoints: 0 };

    console.log("Mevcut streak verisi:", data);
    console.log("Mevcut kullanıcı verisi:", userData);

    const lastStreakDate = data.lastStreakDate ? new Date(data.lastStreakDate) : null;
    const todayDate = new Date(today);
    let newStreak = data.currentStreak || 0;

    if (lastStreakDate) {
      const diffDays = Math.floor(
        (todayDate - lastStreakDate) / (1000 * 60 * 60 * 24)
      );
      console.log(`Gün farkı: ${diffDays}`);
      if (diffDays > 1) {
        newStreak = 0;
      } else if (
        diffDays === 1 &&
        (!data[type] || data[type].lastDate !== today)
      ) {
        newStreak += 1;
      }
    } else {
      newStreak = 1;
    }

    if (!data[type] || data[type].lastDate !== today) {
      data[type] = { count: (data[type]?.count || 0) + 1, lastDate: today };
      data.currentStreak = newStreak;
      data.longestStreak = Math.max(data.longestStreak || 0, newStreak);
      data.lastStreakDate = today;

      userData.weeklyStreakPoints = (userData.weeklyStreakPoints || 0) + 1;
      userData.monthlyStreakPoints = (userData.monthlyStreakPoints || 0) + 1;

      console.log("Güncellenen streak verisi:", data);
      console.log("Güncellenen kullanıcı verisi:", userData);

      await setDoc(docRef, data);
      await setDoc(userRef, userData, { merge: true });
      console.log("Streak ve kullanıcı verisi başarıyla güncellendi.");
    } else {
      console.log("Aynı gün içinde tekrar işlem yapılmış, streak güncellenmedi.");
    }
  } catch (err) {
    console.error("Streak güncellenirken hata:", err);
    throw new Error(`Streak güncelleme başarısız: ${err.message}`);
  }
};

// 🎖️ Rozet kontrolü ve ödül verme
export const checkAndAwardBadge = async (userId, type) => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      throw new Error("Kullanıcı bulunamadı.");
    }

    const userData = userSnap.data();
    const currentStreak = userData.currentStreak || 0;
    const streakBadges = userData.streakBadges || [];
    const newBadges = [];

    for (const badgeId in badgeConfig) {
      const badge = badgeConfig[badgeId];
      if (
        badge.type === type &&
        currentStreak >= badge.threshold &&
        !streakBadges.includes(bageId)
      ) {
        newBadges.push(badgeId);
      }
    }

    if (newBadges.length > 0) {
      await setDoc(
        userRef,
        {
          streakBadges: [...streakBadges, ...newBadges],
          xp: (userData.xp || 0) + newBadges.length * 10,
        },
        { merge: true }
      );
    }
  } catch (err) {
    console.error("Rozet kontrolü sırasında hata:", err);
    throw new Error("Rozet kontrolü başarısız.");
  }
};

// 🌍 Tüm kullanıcıların streak verilerini getir (Yalnızca adminler için)
export const getAllStreaks = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Kullanıcı giriş yapmamış");
    }

    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (!userDoc.exists() || userDoc.data().role !== "admin") {
      throw new Error("Bu işlem yalnızca adminler için geçerlidir.");
    }

    const usersSnap = await getDocs(collection(db, "users"));
    const streaksSnap = await getDocs(collection(db, "streaks"));

    const users = usersSnap.docs.map((doc) => ({
      userId: doc.id,
      username: doc.data().username || "👤 Öğrenci",
      examType: doc.data().examType || "TYT–AYT",
      weeklyStreakPoints: doc.data().weeklyStreakPoints || 0,
      monthlyStreakPoints: doc.data().monthlyStreakPoints || 0,
    }));

    const streaks = streaksSnap.docs.map((doc) => ({
      userId: doc.id,
      currentStreak: doc.data().currentStreak || 0,
    }));

    const allStreaks = users.map((user) => {
      const userStreak = streaks.find((s) => s.userId === user.userId) || {
        currentStreak: 0,
      };
      return {
        userId: user.userId,
        username: user.username,
        examType: user.examType,
        currentStreak: userStreak.currentStreak,
        weeklyStreakPoints: user.weeklyStreakPoints,
        monthlyStreakPoints: user.monthlyStreakPoints,
      };
    });

    return allStreaks;
  } catch (err) {
    console.error("Tüm streak verileri çekilirken hata:", err.message);
    throw new Error("Streak verileri alınamadı.");
  }
};

// 🏆 Liderlik tablosunu getir
export const getLeaderboard = async (examType, period = "weekly", userId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Kullanıcı giriş yapmamış");
    }

    if (!examType || !["weekly", "monthly"].includes(period)) {
      return { top10: [], userRankData: null };
    }

    // Doğrudan users koleksiyonundan çek (Maliyet Optimizasyonu)
    // Eğer index hatası alınırsa Firebase konsolundaki linkten index oluşturulmalı.
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("examType", "==", examType)
    );
    const snapshot = await getDocs(q);

    let usersData = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        userId: doc.id,
        username: data.username || "Bilinmeyen",
        avatar: data.avatar,
        examType: data.examType,
        streakPoints: data[`${period}StreakPoints`] || 0,
      };
    });

    const testUserIds = ["testUser"];
    const testUsernames = ["Test User", "testuser"];
    usersData = usersData.filter(
      (u) =>
        !testUserIds.includes(u.userId) &&
        !testUsernames.includes(u.username)
    );

    usersData.sort((a, b) => b.streakPoints - a.streakPoints);

    // İlk 10'u al
    const top10Users = usersData.slice(0, 10).map((entry, index) => ({
      ...entry,
      rank: index + 1,
    }));

    // Top 10 için streak verilerini çek (N+1 engellendi, sadece 10 kişi için)
    const top10WithStreaks = await Promise.all(
      top10Users.map(async (u) => {
        const streakDoc = await getDoc(doc(db, "streaks", u.userId));
        return {
          ...u,
          currentStreak: streakDoc.exists() ? streakDoc.data().currentStreak || 0 : 0,
        };
      })
    );

    let userRankData = null;
    if (userId) {
      const userIndex = usersData.findIndex((entry) => entry.userId === userId);
      if (userIndex !== -1) {
        // Kullanıcının streak verisini çek
        const myStreakDoc = await getDoc(doc(db, "streaks", userId));
        userRankData = {
          ...usersData[userIndex],
          rank: userIndex + 1,
          currentStreak: myStreakDoc.exists() ? myStreakDoc.data().currentStreak || 0 : 0,
        };
      }
    }

    return { top10: top10WithStreaks, userRankData };
  } catch (err) {
    console.error("Liderlik tablosu hatası:", err.message);
    return { top10: [], userRankData: null };
  }
};

// 🎉 Tebrik gönder
export const sendCongratulation = async (fromUserId, toUserId) => {
  try {
    const congratsRef = collection(db, "congratulations");
    const userRef = doc(db, "users", toUserId);
    const fromUserRef = doc(db, "users", fromUserId);
    const today = new Date().toISOString().split("T")[0];

    // Aynı gün içinde tebrik gönderilip gönderilmediğini kontrol et
    const q = query(
      congratsRef,
      where("fromUserId", "==", fromUserId),
      where("toUserId", "==", toUserId),
      where("date", "==", today)
    );
    const existing = await getDocs(q);
    if (!existing.empty) {
      throw new Error("Bu kullanıcıyı bugün zaten tebrik ettiniz!");
    }

    // Gönderici kullanıcı bilgilerini al
    const fromUserSnap = await getDoc(fromUserRef);
    if (!fromUserSnap.exists()) {
      throw new Error("Gönderici kullanıcı bulunamadı.");
    }
    const fromUsername = fromUserSnap.data().username || "👤 Öğrenci";

    // Tebrik kaydını ekle
    await addDoc(congratsRef, {
      fromUserId,
      toUserId,
      date: today,
      socialPoints: 5,
      timestamp: Timestamp.now(),
    });

    // Alıcı kullanıcıya puan ekle
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      await setDoc(
        userRef,
        {
          socialPoints: (userData.socialPoints || 0) + 5,
          weeklyStreakPoints: (userData.weeklyStreakPoints || 0) + 2,
          monthlyStreakPoints: (userData.monthlyStreakPoints || 0) + 2,
          xp: (userData.xp || 0) + Math.floor((userData.socialPoints || 0 + 5) / 10),
        },
        { merge: true }
      );
    }

    // Bildirim oluştur
    await createNotification({
      userId: toUserId,
      fromUserId,
      fromUsername,
      type: "congratulation",
    });
  } catch (err) {
    console.error("Tebrik gönderilirken hata:", err);
    throw err;
  }
};

// 📅 Haftalık/Aylık sıfırlama (istemci tarafında çağrılmayacak, Cloud Function kullanıyor)
export const resetLeaderboard = async (period = "weekly") => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Kullanıcı giriş yapmamış");
    }

    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (!userDoc.exists() || userDoc.data().role !== "admin") {
      throw new Error("Bu işlem yalnızca adminler için geçerlidir.");
    }

    // Cloud Function'ı çağır
    const resetFunction = await import("firebase/functions").then(({ getFunctions, httpsCallable }) => {
      const functions = getFunctions();
      return httpsCallable(functions, "resetLeaderboard");
    });

    const result = await resetFunction({ period });
    console.log(`${period} liderlik tablosu sıfırlandı:`, result.data);
    return result.data;
  } catch (err) {
    console.error("Liderlik tablosu sıfırlanırken hata:", err);
    throw new Error("Liderlik tablosu sıfırlama başarısız.");
  }
};